import {
    Modal, ModalContent, ModalOverlay,
} from '@chakra-ui/react';
import { ConfirmationDialog } from '@acp/core-ui';
import { AccessIssuesWizardMachineContext } from './machine/context';
import { AccessIssuesWizardLoadingScreen } from './screens/loading-screen';
import { AccessIssuesWizardResolveScreen } from './screens/resolve-screen';
import { AccessIssuesWizardErrorScreen } from './screens/error-screen';
import { AccessIssuesWizardSelectEntryMethodScreen } from './screens/select-entry-method-screen';
import { useIsInResolveMode } from './hooks';
import { AccessIssuesWizardAllMethodsDisqualifiedScreen } from './screens/all-disqualified-screen';

function FatalErrorScreen() {
    const message = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.fatalErrorMessage
    );

    if (message === null) {
        return (
            <AccessIssuesWizardErrorScreen
                title="An unexpected error occurred while resolving access issues."
                description="Please try again later or contact support."
            />
        );
    }

    return (
        <AccessIssuesWizardErrorScreen
            title="An unexpected error occurred while resolving access issues."
            description={`Please try again later or contact support. (${message})`}
        />
    );
}

export function AccessIssuesWizardModal() {
    const machine = AccessIssuesWizardMachineContext.useActorRef();
    const isOpen = AccessIssuesWizardMachineContext.useSelector(
        (state) => !state.matches('idle')
    );

    const isLoading = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.matches('initialLoad')
    );

    const hasNoAccessIssues = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.matches('noAccessIssuesError')
    );

    const allEntryMethodsDisqualified = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.matches('allEntryMethodsDisqualified')
    );

    const isFatalError = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.matches('fatalError')
    );

    const isInResolveMode = useIsInResolveMode();

    const isInSelectEntryMethodMode = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.matches('selectEntryMethod')
    );

    const showCloseConfirmation = AccessIssuesWizardMachineContext.useSelector(
        (state) => state.context.showCloseConfirmation
    );

    return (
        <>
            <Modal
                isOpen={isOpen}
                autoFocus={false}
                onClose={() => machine.send({ type: 'CLOSE_MODAL' })}
                size="4xl"
            >
                <ModalOverlay />
                <ModalContent>
                    { isLoading && <AccessIssuesWizardLoadingScreen description="Getting ready..." /> }
                    { (hasNoAccessIssues) && (
                        <AccessIssuesWizardErrorScreen
                            title="No access issues"
                            description="There are no access issues for this property."
                        />
                    )}
                    { isInResolveMode && <AccessIssuesWizardResolveScreen /> }
                    { isInSelectEntryMethodMode && <AccessIssuesWizardSelectEntryMethodScreen /> }
                    { isFatalError && <FatalErrorScreen /> }
                    {
                        allEntryMethodsDisqualified
                     && <AccessIssuesWizardAllMethodsDisqualifiedScreen />
                    }
                </ModalContent>
            </Modal>
            <ConfirmationDialog
                isOpen={showCloseConfirmation}
                title="Are you sure you want finish resolving access issues?"
                confirmText="Close"
                cancelText="Back"
                onCancel={() => machine.send({ type: 'HIDE_CLOSE_CONFIRMATION' })}
                onConfirm={() => machine.send({ type: 'FORCE_CLOSE_MODAL' })}
                isCentered
                trapFocus={false}
            >
                Any access issues that you have already resolved have been saved.
            </ConfirmationDialog>
        </>
    );
}
